import { DocumentError, QuestionAnswerPair, SignatureStore, FormQuestionComponents } from "@ambii-com/core";
import { FormQuestion } from "@ambii-com/core";
import { FormPage } from "@ambii-com/core";
import { instanceToPlain, plainToInstance, Transform } from "class-transformer";
import { PatientInfoTags, Sex } from "@ambii-com/core";
import {
  PatientInfo,
  Answer,
  AnswerContentBase,
  AddressContent,
  DateContent,
  AppointmentDatetimeContent,
  NameContent,
  NameKanaContent,
  StringAnswerContent,
  ArrayAnswerContent,
  FileAnswerStore,
  SchemaStore,
  EmailAddressContent,
  AppointmentSchedule,
  Appointment
} from "@ambii-com/core";
import dayjs from "dayjs";
import "reflect-metadata";
import { IsOptional, MaxLength, Matches, IsNumberString, IsIn, IsEmail, validateSync, ValidationError } from "class-validator";
import { StringMax, StringMin } from "~/utils/classValidatorCustomDecorators";
import { MAX_BIRTH_DATE_AGE_YEARS } from "~/utils/formValidation";
import { plainToClass, Expose } from "class-transformer";
import { SplitAddressContent } from "~/components/pages/form/questionTypes/addressField.vue";

export interface AppointmentData {
  schedule: AppointmentSchedule,
  appointment: Appointment
}

class ExternalServiceAutofillParams {
  @IsOptional()
  @MaxLength(50)
  @Expose()
  firstName: string

  @IsOptional()
  @MaxLength(50)
  @Expose()
  familyName: string

  @IsOptional()
  @MaxLength(50)
  @Matches(/^[ァ-ヶー　]+$/, { message: 'Must be full-width katakana' })
  @Expose()
  firstNameKana: string

  @IsOptional()
  @MaxLength(50)
  @Matches(/^[ァ-ヶー　]+$/, { message: 'Must be full-width katakana' })
  @Expose()
  familyNameKana: string

  @IsOptional()
  @Matches(/^\d{4}$/)
  @StringMax(new Date().getFullYear())
  @StringMin(new Date().getFullYear() - MAX_BIRTH_DATE_AGE_YEARS)
  @Expose()
  dobYear: string

  @IsOptional()
  @IsNumberString()
  // remove leading zeroes
  @Transform(({ value }) => typeof value === 'string' ? value.replace(/^0+/, ''): undefined)
  @StringMin(1)
  @StringMax(12)
  @Expose()
  dobMonth: string

  @IsOptional()
  @IsNumberString()
  @StringMin(1)
  @StringMax(31)
  @Expose()
  dobDate: string

  @IsOptional()
  @MaxLength(50)
  @Expose()
  cardNumber: string

  @IsOptional()
  @IsIn([Sex.FEMALE, Sex.MALE], { message: `Must be one of ${Object.values(Sex)}`})
  @Expose()
  sex: string

  @IsOptional()
  @Expose()
  homePhone: string

  @IsOptional()
  @Expose()
  cellPhone: string

  @IsOptional()
  @Expose()
  postalCode: string

  @IsOptional()
  @MaxLength(200)
  @Expose()
  prefectureWard: string

  @IsOptional()
  @MaxLength(200)
  @Expose()
  otherAddress: string

  @IsOptional()
  @IsEmail()
  @Expose()
  emailAddress: string

  static createFromURLQuery(params: Record<string, string | string[]>): Partial<ExternalServiceAutofillParams> {
    // filter out params not exposed
    const rawAutofillParams = plainToClass(ExternalServiceAutofillParams, params, { excludeExtraneousValues: true })
    const errors: ValidationError[] = validateSync(rawAutofillParams)
    if(errors.length){
      console.error('Errors found when trying to autofill patient info using URL query', errors)
    }

    // filter out params with errors
    const autofillParams: Partial<ExternalServiceAutofillParams> = Object.keys(rawAutofillParams).reduce((acc, key) => {
      const hasError = errors.some(error => error.property === key);
      if (!hasError) {
        acc[key] = rawAutofillParams[key];
      }
      return acc;
    }, {});
    return autofillParams
  }
  get isEmpty(): boolean {
    return Object.keys(this).length === 0;
  }
}

export class FormSession {
  static pageToPairsMapFromPlain(
    value: object
  ): Map<PageId, Array<QuestionAnswerPair>> {
    if (!value) return undefined;
    return new Map(
      Object.entries(value).map(([pageId, plainObjs]: [PageId, any[]]) => {
        return [
          pageId,
          plainObjs.map((plainObj: { question: Object; answer: Object }) => {
            return plainToInstance(QuestionAnswerPair, plainObj);
          }),
        ];
      })
    );
  }
  @Transform(({ value }) => FormSession.pageToPairsMapFromPlain(value), {
    toClassOnly: true,
  })
  pageToPairsMap: Map<PageId, Array<QuestionAnswerPair>>;
  appointmentData?: AppointmentData
  constructor(pageToPairsMap: Map<PageId, Array<QuestionAnswerPair>>, appointmentData?: AppointmentData) {
    this.pageToPairsMap = pageToPairsMap;
    this.appointmentData = appointmentData
  }
  static initializeFormSessionFromPageIds(pageIds: PageId[]): FormSession {
    const formSession: Map<PageId, Array<QuestionAnswerPair>> = new Map();
    pageIds.forEach((pageId) => {
      formSession.set(pageId, []);
    });
    return new FormSession(formSession);
  }
  static createFromPlain(plain: Object): FormSession {
    if (plain.hasOwnProperty("createdAt")) delete plain["createdAt"];
    return plainToInstance(FormSession, plain);
  }
  get retrieveAllPairs(): QuestionAnswerPair[] {
    return Array.from(this.pageToPairsMap.values()).flat();
  }
  stripUnbackupableContents(): FormSession {
    return new FormSession(new Map(Array.from(this.pageToPairsMap.entries())
      .map(([pageId, pairs]) => {
        return [pageId, pairs.map(pair => pair.stripUnbackupableContent())]
      })), this.appointmentData)
  }
  retrievePairsBeforePage(currentPageId: PageId): QuestionAnswerPair[] {
    const pairs: QuestionAnswerPair[][] = [];
    if (!this.pageToPairsMap.has(currentPageId)) {
      throw new Error(
        `Cannot find supplied pageId:${currentPageId} in FormSession`
      );
    }
    for (const [pageId, questionAnswerPair] of this.pageToPairsMap) {
      if (pageId !== currentPageId) {
        pairs.push(questionAnswerPair);
      } else if (pageId === currentPageId) {
        break;
      }
    }
    return pairs.flat();
  }
  buildQuestionAnswerPairs(
    pageId: PageId,
    documents: (FormQuestion | DocumentError)[],
    appointment?: AppointmentData,
    URLQuery?: Record<string, string | string[]>
  ) {
    const pairs: QuestionAnswerPair[] = [];
    const autofillParams = ExternalServiceAutofillParams.createFromURLQuery(URLQuery)
    documents.forEach((document) => {
      // filter out DocumentErrors for now
      if(document instanceof FormQuestion){
        const pair = new QuestionAnswerPair(document, document.defaultAnswer);
        if(appointment) this.fillPatientInfoFromAppointment(pair, appointment)
        if(!autofillParams.isEmpty) this.fillPatientInfoFromExternalService(pair, autofillParams)
        pairs.push(pair)
      }
    });
    this.pageToPairsMap.set(pageId, pairs);
  }
  fillPatientInfoFromExternalService(pair: QuestionAnswerPair, autofillParams: Partial<ExternalServiceAutofillParams>): QuestionAnswerPair{
    switch (pair.question.dashboardMetadata.patientInfoTag) {
      case PatientInfoTags.NAME:
        if (pair.answer instanceof NameContent && (autofillParams.familyName || autofillParams.firstName)) {
          pair.answer.familyName = `${autofillParams.familyName ?? ''}`;
          pair.answer.firstName = `${autofillParams.firstName ?? ''}`;
        } else if (!(pair.answer instanceof NameContent)) {
          console.warn(
            "expected NameContent when autofilling but got",
            pair.answer.constructor.name
          );
        }
        break;
      case PatientInfoTags.NAME_PRONUNCIATION:
        if (pair.answer instanceof NameKanaContent && (autofillParams.familyNameKana || autofillParams.firstNameKana)) {
          pair.answer.familyNameKana = `${autofillParams.familyNameKana ?? ''}`;
          pair.answer.firstNameKana = `${autofillParams.firstNameKana ?? ''}`;
        } else if (!(pair.answer instanceof NameKanaContent)) {
          console.warn(
            "expected NameKanaContent when autofilling but got",
            pair.answer.constructor.name
          );
        }
        break;
      case PatientInfoTags.CARD_NUMBER:
        if (pair.answer instanceof StringAnswerContent && autofillParams.cardNumber) {
          pair.answer.value = `${autofillParams.cardNumber}`;
        } else if (!(pair.answer instanceof StringAnswerContent)) {
          console.warn(
            "expected StringAnswerContent when autofilling but got",
            pair.answer.constructor.name
          );
        }
        break;
      case PatientInfoTags.DATE_OF_BIRTH:
        if (pair.answer instanceof DateContent && (autofillParams.dobYear || autofillParams.dobMonth || autofillParams.dobDate)) {
          pair.answer.year = `${autofillParams.dobYear ?? ''}`;
          pair.answer.month = `${autofillParams.dobMonth ?? ''}`;
          pair.answer.date = `${autofillParams.dobDate ?? ''}`;
        } else if (!(pair.answer instanceof DateContent)) {
          console.warn(
            "expected DateContent when autofilling but got",
            pair.answer.constructor.name
          );
        }
        break;
      case PatientInfoTags.SEX:
        if (pair.answer instanceof ArrayAnswerContent && autofillParams.sex) {
          pair.answer.values = [`${autofillParams.sex}`];
        } else if (!(pair.answer instanceof ArrayAnswerContent)) {
          console.warn(
            "expected ArrayAnswerContent when autofilling but got",
            pair.answer.constructor.name
          );
        }
        break;
      case PatientInfoTags.HOME_PHONE:
        if (pair.answer instanceof StringAnswerContent && autofillParams.homePhone) {
          pair.answer.value = `${autofillParams.homePhone}`;
        } else if (!(pair.answer instanceof StringAnswerContent)) {
          console.warn(
            "expected StringAnswerContent when autofilling but got",
            pair.answer.constructor.name
          );
        }
        break;
      case PatientInfoTags.CELL_PHONE:
        if (pair.answer instanceof StringAnswerContent && autofillParams.cellPhone) {
          pair.answer.value = `${autofillParams.cellPhone}`;
        } else if (!(pair.answer instanceof StringAnswerContent)) {
          console.warn(
            "expected StringAnswerContent when autofilling but got",
            pair.answer.constructor.name
          );
        }
        break;
      case PatientInfoTags.ADDRESS:
        if (pair.answer instanceof AddressContent && (autofillParams.postalCode || autofillParams.prefectureWard || autofillParams.otherAddress)) {
          const splitAddressContent = pair.answer as SplitAddressContent
          splitAddressContent.postalCode = `${autofillParams.postalCode ?? ''}`;
          splitAddressContent._prefectureWard = `${autofillParams.prefectureWard ?? ''}`;
          splitAddressContent._otherAddress = `${autofillParams.otherAddress ?? ''}`;
          splitAddressContent.address = `${splitAddressContent._prefectureWard} ${splitAddressContent._otherAddress}`;
        } else if (!(pair.answer instanceof AddressContent)) {
          console.warn(
            "expected AddressContent when autofilling but got",
            pair.answer.constructor.name
          );
        }
        break;
      case PatientInfoTags.EMAIL_ADDRESS:
        if (pair.answer instanceof EmailAddressContent && autofillParams.emailAddress) {
          pair.answer.emailAddress = `${autofillParams.emailAddress ?? ''}`;
        } else if (pair.answer instanceof StringAnswerContent && autofillParams.emailAddress) {
          pair.answer.value = `${autofillParams.emailAddress ?? ''}`;
        } else if (!(pair.answer instanceof EmailAddressContent) && !(pair.answer instanceof StringAnswerContent)) {
          console.warn(
            "expected EmailAddressContent or StringAnswerContent when autofilling but got",
            pair.answer.constructor.name
          );
        }
        break;
    }
    return pair;
  }
  fillPatientInfoFromAppointment(pair: QuestionAnswerPair, appointmentData: AppointmentData): QuestionAnswerPair{
    const { schedule, appointment } = appointmentData
    switch (pair.question.dashboardMetadata.patientInfoTag) {
      // case PatientInfoTags.NEW_PATIENT:
      //   break;
      case PatientInfoTags.NAME:
        if (appointment.firstName && appointment.lastName && pair.answer instanceof NameContent) {
          pair.answer.firstName = appointment.firstName
          pair.answer.familyName = appointment.lastName
        }
        break;
      case PatientInfoTags.NAME_PRONUNCIATION:
        if (appointment.firstNameKana && appointment.lastNameKana && pair.answer instanceof NameKanaContent) {
          pair.answer.firstNameKana = appointment.firstNameKana
          pair.answer.familyNameKana = appointment.lastNameKana
        }
        break;
      case PatientInfoTags.CARD_NUMBER:
        if (appointment.cardNumber && pair.answer instanceof StringAnswerContent) {
          pair.answer.value = appointment.cardNumber
        }
        break;
      case PatientInfoTags.DATE_OF_BIRTH:
        if (appointment.dob && pair.answer instanceof DateContent) {
          const date = dayjs(appointment.dob)
          pair.answer.year = date.year().toString()
          pair.answer.month = (date.month() + 1).toString()
          pair.answer.date = date.date().toString()
        }
        break;
      case PatientInfoTags.SEX:
        if (appointment.sex && pair.answer instanceof ArrayAnswerContent) {
          pair.answer.values = [appointment.sex]
        }
        break;
      case PatientInfoTags.CELL_PHONE:
        if (appointment.tel && pair.answer instanceof StringAnswerContent) {
          pair.answer.value = appointment.tel
        }
        break;
      case PatientInfoTags.EMAIL_ADDRESS:
        if (appointment.contactEmail && pair.answer instanceof EmailAddressContent) {
          pair.answer.emailAddress = appointment.contactEmail
        }
        break;
      case PatientInfoTags.APPOINTMENT_DATETIME:
        if (schedule && pair.answer instanceof AppointmentDatetimeContent) {
          const timeSlot = schedule.getTimeSlot(appointment.index)
          pair.answer.hasAppointment = true
          pair.answer.date = dayjs(timeSlot.startDate).format('YYYY-MM-DD')
          pair.answer.time = timeSlot.startTime
        }
        break;
    }
    return pair
  }
  updateQuestionAnswerPairs(pageId: PageId, pairs: QuestionAnswerPair[]) {
    if (!this.pageToPairsMap.has(pageId)) {
      throw new Error("PageId does not exist in FormSession");
    } else {
      this.pageToPairsMap.set(pageId, pairs);
    }
  }
  // Used when editing single answer in confirm page
  editQuestionAnswerPair(
    pageId: PageId,
    newPair: QuestionAnswerPair
  ): FormSession {
    const oldPairs = this.pageToPairsMap.get(pageId);
    if (oldPairs === undefined) {
      throw new Error("PageId does not exist in FormSession");
    }
    const oldPairIndex = oldPairs.findIndex(
      (oldPair) => oldPair.question.id === newPair.question.id
    );
    if (oldPairIndex === undefined) {
      throw new Error("QuestionAnswerPair not found in FormSession");
    }
    oldPairs.splice(oldPairIndex, 1, newPair.clone());
    return new FormSession(this.pageToPairsMap.set(pageId, oldPairs), this.appointmentData);
  }
  createAnswers(formPages: FormPage[], storageFolder: string): Map<string, Array<Answer>> {
    return new Map(
      Array.from(this.pageToPairsMap.entries()).map(
        ([pageId, QuestionAnswerPairs]) => {
          return [
            pageId,
            QuestionAnswerPairs.map((questionAnswerPair) => {
              if(questionAnswerPair.answer instanceof FileAnswerStore){
                questionAnswerPair = questionAnswerPair.convertStoreToContent(storageFolder)
              }
              else if (questionAnswerPair.answer instanceof SignatureStore || questionAnswerPair.answer instanceof SchemaStore) {
                questionAnswerPair = questionAnswerPair.convertStoreToContent(storageFolder)
              }
              return questionAnswerPair.createAnswer(
                this.pageToPairsMap,
                formPages
              );
            }),
          ];
        }
      )
    );
  }
  get extractPatientInfo(): PatientInfo {
    const patientInfo: PatientInfo = {};
    for (const pair of this.retrieveAllPairs) {
      switch (pair.question.dashboardMetadata.patientInfoTag) {
        case PatientInfoTags.NEW_PATIENT:
          patientInfo.newPatient = pair.mapToMedicalTerms
            ? pair.mapToMedicalTerms.toDisplayText
            : pair.answer.toDisplayText;
          break;
        case PatientInfoTags.NAME:
          patientInfo.familyName = (pair.answer as NameContent).familyName;
          patientInfo.firstName = (pair.answer as NameContent).firstName;
          break;
        case PatientInfoTags.NAME_PRONUNCIATION:
          patientInfo.familyNameKana = (
            pair.answer as NameKanaContent
          ).familyNameKana;
          patientInfo.firstNameKana = (
            pair.answer as NameKanaContent
          ).firstNameKana;
          break;
        case PatientInfoTags.CARD_NUMBER:
          patientInfo.cardNumber = pair.answer.toDisplayText;
          break;
        case PatientInfoTags.DATE_OF_BIRTH:
          patientInfo.dob = pair.answer.toDisplayText;
          break;
        case PatientInfoTags.SEX:
          patientInfo.sex = pair.mapToMedicalTerms
            ? pair.mapToMedicalTerms.toDisplayText
            : pair.answer.toDisplayText;
          break;
        case PatientInfoTags.HOME_PHONE:
          patientInfo.homePhone = pair.answer.toDisplayText;
          break;
        case PatientInfoTags.CELL_PHONE:
          patientInfo.cellPhone = pair.answer.toDisplayText;
          break;
        case PatientInfoTags.EMAIL_ADDRESS:
          patientInfo.emailAddress = pair.answer.toDisplayText;
          break;
        case PatientInfoTags.ADDRESS:
          patientInfo.address = (pair.answer as AddressContent).toDisplayText;
          break;
        case PatientInfoTags.APPOINTMENT_DATETIME:
          patientInfo.appointmentDatetime = (
            pair.answer as AppointmentDatetimeContent
          ).toDisplayText;
          break;
        case PatientInfoTags.EMAIL_ADDRESS:
          patientInfo.emailAddress = (pair.answer as EmailAddressContent).toDisplayText;
          break;
      }
    }
    return patientInfo;
  }
  get filesForStorage(): File[] {
    return this.retrieveAllPairs
      .map((pair) => pair.answer)
      .filter((answer: AnswerContentBase) => answer instanceof FileAnswerStore)
      .flatMap((answer: FileAnswerStore) => {
          return answer.allFiles
      })
  }
  get signaturePairs(): QuestionAnswerPair[] {
    return this.retrieveAllPairs
      .filter((pair: QuestionAnswerPair) => pair.answer instanceof SignatureStore)
  }
  get schemaPairs(): QuestionAnswerPair[] {
    return this.retrieveAllPairs
      .filter((pair: QuestionAnswerPair) => pair.answer instanceof SchemaStore)
  }
  get uploadFieldPairs(): QuestionAnswerPair[] {
    return this.retrieveAllPairs
      .filter((pair: QuestionAnswerPair) => pair.question.componentType === FormQuestionComponents.UPLOAD_FIELD)
  }
  convertToObject(): Object {
    return { createdAt: Date.now(), ...instanceToPlain(this) };
  }
}
